/** The progress of the sandbox asked for at sign-up (OnboardingSandbox, backend ESB) in words: the Onboarding page's
 *  block (SandboxSetup) and the Developer Overview's sandbox card share them. No React, no imports of either page. */
import { SANDBOX_STATUSES, sandboxErrorWords } from './labels';
import type { OnboardingLaneState, OnboardingSandbox } from './types';

/** The set-up's steps, in order (`failed` is not one: it marks the step it stopped at). */
export const SANDBOX_STEPS = ['requested', 'creating', 'syncing', 'ready'] as const;

/** Each step's state: done before the current one, the current one running (failed: an error at the step it stopped
 *  at — creating until the sandbox's booking import key is set up, as the backend's status: the tenant, then its
 *  content copy; else syncing); `requested` is done as soon as it is asked for. */
export function sandboxStepStates(sandbox: Pick<OnboardingSandbox, 'status' | 'steps'>): OnboardingLaneState[] {
    const { status } = sandbox;
    if (status === 'failed') {
        const at = sandbox.steps?.source ? 2 : 1;
        return SANDBOX_STEPS.map((_, i) => {
            if (i < at) {
                return 'done';
            }
            return i === at ? 'error' : 'pending';
        });
    }
    if (status === 'ready') {
        return SANDBOX_STEPS.map(() => 'done');
    }
    const at = Math.max(0, SANDBOX_STEPS.indexOf(status as typeof SANDBOX_STEPS[number]));
    return SANDBOX_STEPS.map((step, i) => {
        if (i < at || step === 'requested') {
            return 'done';
        }
        return i === at ? 'running' : 'pending';
    });
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What was synced so far ("1 tour, 3 bookings"), or null before anything was counted. */
export function sandboxSyncedWords(steps: OnboardingSandbox['steps'] | null | undefined): string | null {
    const parts: string[] = [];
    if (steps && typeof steps.products === 'number') {
        parts.push(plural(steps.products, 'tour', 'tours'));
    }
    if (steps && typeof steps.bookings === 'number') {
        parts.push(plural(steps.bookings, 'booking', 'bookings'));
    }
    return parts.length ? parts.join(', ') : null;
}

/** One line for the set-up (the Developer Overview's sandbox card): the status in words, what was synced, why it failed. */
export function sandboxSetupLine(sandbox: OnboardingSandbox): string {
    const status = SANDBOX_STATUSES[sandbox.status || 'requested'] || String(sandbox.status);
    if (sandbox.status === 'failed') {
        return `${status}: ${sandboxErrorWords(sandbox.error)}`;
    }
    const synced = sandboxSyncedWords(sandbox.steps);
    return synced && (sandbox.status === 'syncing' || sandbox.status === 'ready') ? `${status} (${synced})` : status;
}

/** A step's state in words, for screen readers (the icon shows it). */
export const SANDBOX_STEP_STATE_WORDS: Record<string, string> = {
    done: 'done',
    running: 'in progress',
    pending: 'waiting',
    error: 'failed',
};
